'use client'

/**
 * AI Chat Conversation List (Issue #4429, #4443, #4435)
 *
 * Collapsible list of conversations with archive/unarchive
 * and delete with confirmation dialog.
 */

import { useCallback, useMemo, useState, useId } from 'react'
import {
  Plus,
  Archive,
  ArchiveRestore,
  Trash2,
  MessageSquare,
  AlertTriangle,
} from '@/components/icons'
import { DisclosureChevron } from '@/components/ui/disclosure-chevron'
import { Modal } from '@/components/ui/modal'
import { listArchivedAiChatConversations } from '@/app/actions/ai-chat'
import { reportClientError } from '@/lib/client-error'
import { ERROR_IDS } from '@/lib/errors/constants'
import { useAiChat, type ChatConversation } from './ai-chat-provider'

export function AiChatConversationList() {
  const listId = useId()
  const {
    conversations,
    activeConversationId,
    selectConversation,
    startNewChat,
    archiveConversation,
    unarchiveConversation,
    removeConversation,
    refreshConversations,
  } = useAiChat()

  const [isExpanded, setIsExpanded] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [archivedConversations, setArchivedConversations] = useState<
    ChatConversation[]
  >([])
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    title: string
  } | null>(null)

  const sortedConversations = useMemo(
    () =>
      [...conversations].sort(
        (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
      ),
    [conversations],
  )

  const loadArchived = useCallback(async () => {
    try {
      const result = await listArchivedAiChatConversations()
      if (!result.success) {
        throw new Error(result.error)
      }
      setArchivedConversations(
        result.data.map(c => ({
          ...c,
          createdAt: new Date(c.createdAt),
          updatedAt: new Date(c.updatedAt),
        })),
      )
    } catch (error) {
      reportClientError({
        errorId: ERROR_IDS.AI_CHAT_CONVERSATION_LIST_LOAD_ARCHIVED_FAILED,
        action: 'loadArchived',
        error,
      })
      // Silently fail
    }
  }, [])

  const handleToggleArchived = useCallback(async () => {
    if (!showArchived) {
      await loadArchived()
    }
    setShowArchived(prev => !prev)
  }, [showArchived, loadArchived])

  const handleUnarchive = useCallback(
    async (id: string) => {
      await unarchiveConversation(id)
      setArchivedConversations(prev => prev.filter(c => c.id !== id))
      await refreshConversations()
    },
    [unarchiveConversation, refreshConversations],
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return
    await removeConversation(deleteTarget.id)
    setDeleteTarget(null)
    if (showArchived) {
      setArchivedConversations(prev =>
        prev.filter(c => c.id !== deleteTarget.id),
      )
    }
  }, [deleteTarget, removeConversation, showArchived])

  return (
    <>
      <div
        data-testid="ai-chat-conversation-list"
        className="flex flex-col border-b border-stroke-light bg-surface-panel"
      >
        <div className="flex items-center justify-between px-3 py-2">
          <button
            aria-controls={isExpanded ? listId : undefined}
            onClick={() => setIsExpanded(prev => !prev)}
            className="pointer-coarse:min-h-11 flex items-center gap-1 text-xs font-medium text-text-secondary transition-colors hover:text-text-primary"
            type="button"
            aria-expanded={isExpanded}
            // No aria-label: the visible "Conversations (n)" names the
            // toggle and aria-expanded carries its state. An aria-label that
            // omitted the visible word failed WCAG 2.5.3 (#16233).
          >
            Conversations
            {conversations.length > 0 && (
              <span className="ml-1 text-text-secondary">
                ({conversations.length})
              </span>
            )}
            <DisclosureChevron open={isExpanded} className="h-3 w-3" />
          </button>
          <button
            onClick={startNewChat}
            className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-fill-10"
            aria-label="New chat"
            type="button"
          >
            <Plus className="h-3.5 w-3.5 text-text-secondary" />
          </button>
        </div>
        {isExpanded && (
          <div id={listId} className="max-h-48 overflow-y-auto px-1 pb-2">
            {sortedConversations.length === 0 ? (
              <p className="px-2 py-1 text-xs text-text-secondary">
                No conversations yet
              </p>
            ) : (
              sortedConversations.map(convo => (
                <ConversationItem
                  key={convo.id}
                  conversation={convo}
                  isActive={convo.id === activeConversationId}
                  onSelect={() => selectConversation(convo.id)}
                  onArchive={() => archiveConversation(convo.id)}
                  onDelete={() =>
                    setDeleteTarget({
                      id: convo.id,
                      title: convo.title,
                    })
                  }
                />
              ))
            )}

            {/* Archived section */}
            <button
              onClick={handleToggleArchived}
              className="mt-1 flex w-full items-center gap-1 rounded px-2 py-1 text-xs text-text-secondary transition-colors hover:text-text-secondary"
              type="button"
            >
              <Archive className="h-3 w-3" />
              {showArchived ? 'Hide archived' : 'Show archived'}
            </button>

            {showArchived && archivedConversations.length > 0 && (
              <div className="mt-1 space-y-0.5 border-t border-stroke-extra-light pt-1">
                {archivedConversations.map(convo => (
                  <div
                    key={convo.id}
                    className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-text-secondary"
                  >
                    <MessageSquare className="h-3 w-3 shrink-0 opacity-50" />
                    <span className="flex-1 truncate text-xs italic">
                      {convo.title}
                    </span>
                    <div className="flex shrink-0 gap-0.5 opacity-40 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                      <button
                        onClick={() => handleUnarchive(convo.id)}
                        className="flex h-6 w-6 items-center justify-center rounded hover:bg-fill-10"
                        aria-label="Unarchive"
                        type="button"
                        title="Unarchive"
                      >
                        <ArchiveRestore className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() =>
                          setDeleteTarget({
                            id: convo.id,
                            title: convo.title,
                          })
                        }
                        className="flex h-6 w-6 items-center justify-center rounded hover:bg-status-red-bg"
                        aria-label="Delete"
                        type="button"
                      >
                        <Trash2 className="h-3 w-3 text-status-red" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        ariaLabelledBy="delete-dialog-title"
        size="sm"
        footer={
          <>
            <button
              onClick={() => setDeleteTarget(null)}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-text-primary transition-colors hover:bg-fill-10"
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="rounded-md bg-status-red-bg-emphasis px-3 py-1.5 text-xs font-medium text-text-primary-reversed transition-colors hover:bg-status-red-bg-emphasis-hover"
              type="button"
            >
              Delete
            </button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-status-red-bg">
            <AlertTriangle className="h-4 w-4 text-status-red-text" />
          </div>
          <div>
            <h3
              id="delete-dialog-title"
              className="text-sm font-semibold text-text-primary"
            >
              Delete conversation?
            </h3>
            <p className="mt-1 text-xs text-text-secondary">
              &ldquo;{deleteTarget?.title}&rdquo; will be permanently deleted.
              This cannot be undone.
            </p>
          </div>
        </div>
      </Modal>
    </>
  )
}

function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onArchive,
  onDelete,
}: {
  conversation: ChatConversation
  isActive: boolean
  onSelect: () => void
  onArchive: () => void
  onDelete: () => void
}) {
  return (
    <div
      className={`group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors ${
        isActive
          ? 'bg-status-blue-bg-light text-status-blue-text-strong  '
          : 'text-text-primary hover:bg-fill-10'
      }`}
    >
      <button
        onClick={onSelect}
        className="flex flex-1 items-center gap-2 overflow-hidden text-left"
        type="button"
      >
        <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-50" />
        <span className="truncate text-xs">{conversation.title}</span>
      </button>
      <div className="flex shrink-0 gap-0.5 opacity-40 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
        <button
          onClick={e => {
            e.stopPropagation()
            onArchive()
          }}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-fill-10"
          aria-label="Archive conversation"
          type="button"
          title="Archive"
        >
          <Archive className="h-3 w-3" />
        </button>
        <button
          onClick={e => {
            e.stopPropagation()
            onDelete()
          }}
          className="flex h-6 w-6 items-center justify-center rounded hover:bg-status-red-bg"
          aria-label="Delete conversation"
          type="button"
          title="Delete"
        >
          <Trash2 className="h-3 w-3 text-status-red" />
        </button>
      </div>
    </div>
  )
}
